/**
 * Retained bundle-chunk registry for background prefetch.
 *
 * Bundle delivery streams nothing speculatively: a `background` page releases
 * its rows and fetches no chunk. What it used to throw away is the manifests
 * themselves — the signed chunk URLs that would
 * let the prefetcher fetch that page's bytes later, when it actually reaches
 * the series. This module is where those manifests go instead of the floor.
 *
 * Retention is keyed by each member's dataset-cache key, not by page index.
 * `SeriesBundleChunk.offset`/`count` index into the PAGE's images, while the
 * prefetcher walks the series' full imageId list — which is spliced, pinned
 * and healed as pages land — so a page-relative offset does not survive to
 * when the prefetcher gets there. The dataset-cache key is the one derivation
 * both the seeding side (./datasetSeed) and the loader itself agree on, and it
 * already collapses every frame of a multi-frame instance onto one key, so a
 * single lookup serves every frame.
 *
 * Retention happens only when `shouldBundlePage` says the page is bundleable
 * — the same predicate delivery uses to decide whether to bundle-stream at
 * all, so a page delivery already declined to bundle retains nothing here.
 *
 * See docs/planning/2026-08-25-bundle-aware-prefetch.md.
 */
import * as telemetry from '@/lib/telemetry';
import { shouldBundlePage } from './bundleLoader';
import { datasetCacheKey } from './datasetSeed';
/**
 * How long a retained chunk stays usable. Manifests carry a one-hour signing
 * TTL server-side; retained past that, a fetch 403s. Past this age the
 * registry treats the chunk as absent — no request, straight to per-object —
 * rather than let the prefetcher hit an expired signature. Shorter than the
 * server TTL so ordinary reads never race the expiry, per the design doc's
 * "manifest expiry" note.
 */
export const RETAINED_CHUNK_MAX_AGE_MS = 45 * 60 * 1000;
/** Dataset-cache key -> the chunk that covers it. */
const registry = new Map();
/**
 * Record a page's chunk manifests for a later background fetch.
 *
 * Calling this again for images already retained — a heal re-read's
 * `rows-only` page, or a re-paged series — overwrites their entries with the
 * newer manifest and a fresh `retainedAt`. That is also how a heal re-read
 * re-arms the expiry clock for the pages it touches: no special-case code,
 * just the same key winning twice.
 *
 * No-ops (retains nothing) when `shouldBundlePage` says the page isn't
 * bundleable — LAN-served, video/microscopy, no chunks, or the series'
 * breaker has tripped — so the prefetcher never tries a bundle delivery
 * already declined.
 */
export function retainPage(options) {
    const { seriesId, images, bundles, isVideo, isMicroscopy } = options;
    if (!shouldBundlePage({ seriesId, images, bundles, isVideo, isMicroscopy }))
        return;
    const retainedAt = Date.now();
    for (const chunk of bundles) {
        const slice = images.slice(chunk.offset, chunk.offset + chunk.count);
        if (slice.length === 0)
            continue;
        const entry = { seriesId, chunk, images: slice, retainedAt };
        for (const key of memberKeys(entry)) {
            registry.set(key, entry);
        }
    }
}
/**
 * Every cache key an entry is filed under — one per image in `entry.images`,
 * the same derivation `retainPage` uses to write them. `chunkFor` reuses this
 * on the way out so an aged-out entry is evicted from every key it was
 * retained under, not just the one that was looked up.
 */
function memberKeys(entry) {
    // An instance with no address yet has no key (datasetCacheKey returns empty).
    // Filing a chunk under that would hand it to the first other such instance
    // that looked itself up.
    return entry.images.map(datasetCacheKey).filter((key) => key !== '');
}
/**
 * Remove `entry` from the registry under every key it was retained under —
 * but only where that key still points at THIS entry. A member key can have
 * been re-retained by a fresher chunk since (heal re-reads overwrite
 * per-key); evicting the stale entry must not clobber a key that now points
 * at that newer, still-live entry.
 */
function evictEntry(entry) {
    for (const key of memberKeys(entry)) {
        if (registry.get(key) === entry) {
            registry.delete(key);
        }
    }
}
/**
 * The retained chunk covering `cacheKey`, or `undefined` when nothing usable
 * covers it — no chunk was ever retained for it, or the one that was has
 * aged past {@link RETAINED_CHUNK_MAX_AGE_MS}. Both cases are meant to be
 * indistinguishable to the caller: "no chunk" means "go per object", nothing
 * more specific.
 *
 * An aged-out entry is evicted from the registry under every cache key it
 * was retained under — a chunk covers several member images, and
 * `retainPage` filed the same entry under each of their keys — not just
 * `cacheKey`, so it does not keep being returned (and skipped) under its
 * other keys for the rest of the session. See {@link evictEntry} for how a
 * key re-retained by a fresher chunk since is left untouched.
 */
export function chunkFor(cacheKey) {
    const entry = registry.get(cacheKey);
    if (!entry)
        return undefined;
    if (Date.now() - entry.retainedAt > RETAINED_CHUNK_MAX_AGE_MS) {
        evictEntry(entry);
        // Item 5 of the design doc's five telemetry signals: how often 45 minutes
        // turns out to be too generous (or the manifest TTL too short) for an
        // ordinary read, surfaced as manifest_expired_skips. Fires once per
        // expired chunk here — evictEntry above means a later touch under one of
        // that same chunk's other member keys won't find it in the registry to
        // fire again.
        telemetry.recordManifestExpiredSkip();
        return undefined;
    }
    return entry;
}
/** Number of cache keys currently retained — for tests and diagnostics. */
export function retainedKeyCount() {
    return registry.size;
}
/**
 * Forget every retained chunk. Call on study/case change alongside the
 * dataset-cache purge and the bundle-loader breaker reset (see
 * `resetBundleLoaderState` in ./bundleLoader) — a registry entry outliving its
 * study points a future chunk fetch at another study's manifests — and
 * between tests.
 */
export function resetBundleRegistry() {
    registry.clear();
}
